import type { JobsApi } from '../api/job-api'
import {
  MarkAccessAsGrantedRequest,
  OtherPlatformConnection,
  ProvidePlatformConnectionRequest,
  QuickBooksPlatformConnection,
  XeroPlatformConnection,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import type { AvailableBookkeepingSoftwareType } from '@/features/bookkeeping/platform-connections/available-bookkeeping-software'
import type { PlatformConnectionSelection } from '@/features/bookkeeping/components/BookkeepingPlatformSelectionDialog/BookkeepingPlatformSelectionDialogViewModel'

/**
 * The result of providing a platform connection.
 */
interface ProvidePlatformConnectionResult {
  confirmAccessRequired: boolean
}

export class BookkeepingStore {
  constructor(private readonly _api: JobsApi) {}

  /**
   * Provides the platform connection.
   */
  async providePlatformConnection(
    jobId: Id,
    connection: PlatformConnectionSelection,
  ): Promise<ProvidePlatformConnectionResult> {
    switch (connection.type) {
      case 'QuickBooks':
        await this.provideQuickBooksPlatformConnection(
          jobId,
          connection.account.connectionId,
        )
        return {
          confirmAccessRequired: true,
        }
      case 'Xero':
        await this.provideXeroPlatformConnection(
          jobId,
          connection.account.connectionId,
        )
        return {
          confirmAccessRequired: true,
        }
      case 'Other':
        await this.provideOtherConnection(jobId, connection.software)
        return {
          confirmAccessRequired: false,
        }
    }
  }

  /**
   * Provides QuickBooks platform connection for a given job ID.
   */
  async provideQuickBooksPlatformConnection(jobId: Id, connectionId: Id) {
    const request = new ProvidePlatformConnectionRequest().setId(
      Id.assert(jobId),
    )

    request.setQuickBooks(
      new QuickBooksPlatformConnection().setConnectionId(
        Id.assert(connectionId),
      ),
    )

    await this._api.bookkeeping.providePlatformConnection(request)
  }

  /**
   * Provides Xero platform connection for a given job ID.
   */
  async provideXeroPlatformConnection(jobId: Id, connectionId: Id) {
    const request = new ProvidePlatformConnectionRequest().setId(
      Id.assert(jobId),
    )

    request.setXero(
      new XeroPlatformConnection().setConnectionId(Id.assert(connectionId)),
    )

    await this._api.bookkeeping.providePlatformConnection(request)
  }

  /**
   * Provides other platform connection for a given job ID.
   */
  async provideOtherConnection(
    jobId: Id,
    availableBookkeepingSoftwareType: AvailableBookkeepingSoftwareType,
  ) {
    const request = new ProvidePlatformConnectionRequest().setId(
      Id.assert(jobId),
    )

    request.setOther(
      new OtherPlatformConnection().setInfo(
        this._mapAvailableBookkeepingSoftwareTypeToInfo(
          availableBookkeepingSoftwareType,
        ),
      ),
    )

    await this._api.bookkeeping.providePlatformConnection(request)
  }

  /**
   * Marks the access as granted.
   */
  async markAccessAsGranted(jobId: Id) {
    const request = new MarkAccessAsGrantedRequest().setId(Id.assert(jobId))

    await this._api.bookkeeping.markAccessAsGranted(request)
  }

  /**
   * Maps {@link AvailableBookkeepingSoftwareType} to a valid info.
   */
  private _mapAvailableBookkeepingSoftwareTypeToInfo(
    type: AvailableBookkeepingSoftwareType,
  ) {
    return match(type, {
      Freshbooks: 'FRESHBOOKS',
      Puzzle: 'PUZZLE',
      Sage: 'SAGE',
      Wave: 'WAVE',
      XeroTaxfyleOffering: 'XERO_TAXFYLE_OFFERING',
      QuickBooksDesktop: 'QUICKBOOKS_DESKTOP',
      Other: 'OTHER',
    })
  }
}
